import { useState, useEffect, useCallback } from 'react';
import { formatPrice, formatPriceRange } from '../lib/format';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { ArrowLeft, ArrowRight, Loader2, Check } from 'lucide-react';
import { createBlueprintSession, updateBlueprintSession, getBlueprintSession, completeBlueprintSession, trackEvent } from '../lib/api';
import SEO from '../components/SEO';
import { GA_EVENTS } from '../lib/analytics';

const TOTAL_STEPS = 6;

const PRIORITY_OPTIONS = [
  { value: 'schools_strong', label: 'Strong schools' },
  { value: 'commuter_friendly', label: 'Easy commute to Toronto' },
  { value: 'family_vibe', label: 'Family-friendly feel' },
  { value: 'walkable', label: 'Walkable area' },
  { value: 'quiet_streets', label: 'Quiet residential streets' },
  { value: 'parks_trails', label: 'Parks and trails access' },
  { value: 'newer_homes', label: 'Newer homes / modern builds' },
  { value: 'character_homes', label: 'Character homes / mature trees' },
  { value: 'condo_lifestyle', label: 'Condo / low-maintenance living' },
  { value: 'luxury_upmarket', label: 'Luxury / upmarket area' },
  { value: 'lake_access', label: 'Lake / waterfront access' },
  { value: 'value_affordable', label: 'Affordable / value pricing' },
  { value: 'space_large_lots', label: 'Large lots / outdoor space' },
];

const STEP_META = [
  { title: 'Location', subtitle: 'Where are you currently based?' },
  { title: 'Timeline', subtitle: 'When are you looking to move?' },
  { title: 'Budget', subtitle: 'What\'s your price range?' },
  { title: 'Priorities', subtitle: 'What matters most to you?' },
  { title: 'Family', subtitle: 'Tell us about your household' },
  { title: 'Readiness', subtitle: 'How ready are you?' },
];

export default function BlueprintFlow() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [sessionToken, setSessionToken] = useState(null);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [answers, setAnswers] = useState({
    relocation_origin: '',
    timeline: '',
    budget_min: 500000,
    budget_max: 1200000,
    property_type: '',
    beds_min: 3,
    baths_min: 2,
    priorities: [],
    kids_ages: [],
    school_preference: 'none',
    preapproval_status: '',
    working_with_realtor: false,
  });

  useEffect(() => {
    trackEvent({ event_type: 'blueprint_started', page: 'blueprint' });
    GA_EVENTS.blueprintStarted();
    const saved = localStorage.getItem('blueprint_session');
    if (saved) {
      getBlueprintSession(saved)
        .then(r => {
          setSessionToken(saved);
          setStep(r.data.current_step || 1);
          if (r.data.answers_draft) setAnswers(prev => ({ ...prev, ...r.data.answers_draft }));
        })
        .catch(() => { localStorage.removeItem('blueprint_session'); initSession(); });
    } else {
      initSession();
    }
  }, []);

  const initSession = async () => {
    try {
      const r = await createBlueprintSession({ city: 'burlington' });
      setSessionToken(r.data.session_token);
      localStorage.setItem('blueprint_session', r.data.session_token);
    } catch (e) { console.error('Failed to create session', e); }
  };

  const saveProgress = useCallback(async (nextStep) => {
    if (!sessionToken) return;
    setSaving(true);
    try {
      await updateBlueprintSession(sessionToken, { current_step: nextStep, answers_draft: answers });
    } catch (e) { console.error('Save failed', e); }
    setSaving(false);
  }, [sessionToken, answers]);

  const handleNext = async () => {
    if (step < TOTAL_STEPS) {
      await saveProgress(step + 1);
      GA_EVENTS.blueprintStepCompleted(step);
      setStep(step + 1);
    } else {
      setCompleting(true);
      await saveProgress(step);
      try {
        const r = await completeBlueprintSession(sessionToken);
        trackEvent({ event_type: 'blueprint_completed', session_token: sessionToken });
        GA_EVENTS.blueprintCompleted();
        localStorage.setItem('blueprint_results', JSON.stringify(r.data));
        navigate('/capture');
      } catch (e) { console.error('Complete failed', e); }
      setCompleting(false);
    }
  };

  const handleBack = () => { if (step > 1) { setStep(step - 1); saveProgress(step - 1); } };
  const updateAnswer = (key, value) => setAnswers(prev => ({ ...prev, [key]: value }));
  const togglePriority = (value) => {
    setAnswers(prev => ({
      ...prev,
      priorities: prev.priorities.includes(value)
        ? prev.priorities.filter(p => p !== value)
        : [...prev.priorities, value]
    }));
  };

  const canProceed = () => {
    switch (step) {
      case 1: return answers.relocation_origin !== '';
      case 2: return answers.timeline !== '';
      case 3: return answers.budget_min > 0 && answers.budget_max > answers.budget_min;
      case 4: return answers.priorities.length > 0;
      case 5: return true;
      case 6: return answers.preapproval_status !== '';
      default: return true;
    }
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="blueprint-flow">
      <SEO
        title="Burlington Buyer Blueprint — Find Your Neighbourhood Match"
        description="Answer 6 questions about your budget, timeline, and family priorities. Get matched with the Burlington neighbourhoods that fit — scored 0-100 with honest tradeoffs."
        canonical="/blueprint"
      />
      {/* Left sidebar - Step progress */}
      <aside className="hidden lg:flex w-80 bg-slate-950 flex-col p-8 relative noise-overlay">
        <div className="relative z-10">
          <button onClick={() => navigate('/')} className="flex items-center gap-2.5 mb-16 group" data-testid="exit-blueprint">
            <div className="w-7 h-7 rounded-full bg-white text-slate-900 flex items-center justify-center text-xs font-bold">B</div>
            <span className="text-sm font-semibold text-white/80 group-hover:text-white transition-colors">Buy In Burlington</span>
          </button>

          <div className="space-y-1">
            {STEP_META.map((s, i) => {
              const num = i + 1;
              const isActive = num === step;
              const isDone = num < step;
              return (
                <div key={i} className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-300 ${isActive ? 'bg-white/10' : ''}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                    isDone ? 'bg-emerald-500 text-white' :
                    isActive ? 'bg-white text-slate-900' :
                    'bg-white/10 text-white/30'
                  }`}>
                    {isDone ? <Check className="w-3.5 h-3.5" /> : num}
                  </div>
                  <div>
                    <p className={`text-sm font-medium transition-colors ${isActive ? 'text-white' : isDone ? 'text-white/60' : 'text-white/25'}`}>
                      {s.title}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative z-10 mt-auto pt-8 border-t border-white/10">
          <p className="text-[11px] text-white/20 leading-relaxed">
            Your answers are saved automatically. You can resume within 24 hours.
          </p>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-[10px] font-bold">B</div>
            <span className="text-sm font-semibold text-slate-900">Blueprint</span>
          </button>
          <span className="font-mono text-xs text-slate-400" data-testid="step-indicator">Step {step}/{TOTAL_STEPS}</span>
        </header>

        {/* Progress bar (mobile) */}
        <div className="lg:hidden h-0.5 bg-slate-100">
          <div className="h-full bg-slate-900 transition-all duration-500" style={{ width: `${(step / TOTAL_STEPS) * 100}%` }} data-testid="progress-bar" />
        </div>

        {/* Step content */}
        <div className="flex-1 flex items-center justify-center px-6 py-12 lg:py-0">
          <div className="w-full max-w-md animate-fade-up" key={step}>

            {/* Step 1 - Origin */}
            {step === 1 && (
              <div data-testid="step-1">
                <p className="font-mono text-xs text-slate-400 mb-2">01</p>
                <h2 className="text-3xl md:text-4xl tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  Where are you <span className="italic">moving from?</span>
                </h2>
                <p className="text-sm text-slate-500 mb-8">This helps us understand your context.</p>
                <RadioGroup value={answers.relocation_origin} onValueChange={(v) => updateAnswer('relocation_origin', v)} className="space-y-2">
                  {['Toronto', 'Mississauga / Oakville', 'Hamilton', 'Other GTA', 'Outside Ontario', 'Already in Burlington'].map(opt => (
                    <label
                      key={opt}
                      className="option-card flex items-center gap-3 p-4 rounded-xl cursor-pointer"
                      data-selected={answers.relocation_origin === opt}
                      data-testid={`origin-${opt.toLowerCase().replace(/[^a-z]/g, '-')}`}
                    >
                      <RadioGroupItem value={opt} className="sr-only" />
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        answers.relocation_origin === opt ? 'border-slate-900 bg-slate-900' : 'border-slate-300'
                      }`}>
                        {answers.relocation_origin === opt && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-sm font-medium text-slate-700">{opt}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Step 2 - Timeline */}
            {step === 2 && (
              <div data-testid="step-2">
                <p className="font-mono text-xs text-slate-400 mb-2">02</p>
                <h2 className="text-3xl md:text-4xl tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  What's your <span className="italic">timeline?</span>
                </h2>
                <p className="text-sm text-slate-500 mb-8">When are you looking to make the move?</p>
                <RadioGroup value={answers.timeline} onValueChange={(v) => updateAnswer('timeline', v)} className="grid grid-cols-2 gap-3">
                  {[
                    { value: '0_3', label: 'Within 3 months', sub: 'Active' },
                    { value: '3_6', label: '3–6 months', sub: 'Planning' },
                    { value: '6_12', label: '6–12 months', sub: 'Exploring' },
                    { value: 'researching', label: 'Just researching', sub: 'Early stage' },
                  ].map(opt => (
                    <label
                      key={opt.value}
                      className="option-card p-5 rounded-xl cursor-pointer text-center"
                      data-selected={answers.timeline === opt.value}
                      data-testid={`timeline-${opt.value}`}
                    >
                      <RadioGroupItem value={opt.value} className="sr-only" />
                      <p className="text-sm font-medium text-slate-700">{opt.label}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{opt.sub}</p>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Step 3 - Budget */}
            {step === 3 && (
              <div data-testid="step-3">
                <p className="font-mono text-xs text-slate-400 mb-2">03</p>
                <h2 className="text-3xl md:text-4xl tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  What's your <span className="italic">budget?</span>
                </h2>
                <p className="text-sm text-slate-500 mb-8">Set your comfortable price range.</p>
                <div className="space-y-8">
                  <div className="bg-slate-50 rounded-2xl p-6">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-xs text-slate-500 font-medium">Range</span>
                      <span className="font-mono text-lg font-semibold text-slate-900" data-testid="budget-min-display">
                        {formatPrice(answers.budget_min)} — {formatPrice(answers.budget_max)}
                      </span>
                    </div>
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between mb-2">
                          <Label className="text-xs text-slate-400">Minimum</Label>
                        </div>
                        <Slider
                          value={[answers.budget_min]}
                          onValueChange={([v]) => updateAnswer('budget_min', v)}
                          min={200000} max={3000000} step={50000}
                          className="w-full"
                          data-testid="budget-min-slider"
                        />
                      </div>
                      <div>
                        <div className="flex justify-between mb-2">
                          <Label className="text-xs text-slate-400">Maximum</Label>
                        </div>
                        <Slider
                          value={[answers.budget_max]}
                          onValueChange={([v]) => updateAnswer('budget_max', v)}
                          min={200000} max={3000000} step={50000}
                          className="w-full"
                          data-testid="budget-max-slider"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 font-medium mb-2.5 block">Property type</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { value: 'detached', label: 'Detached' },
                        { value: 'town', label: 'Townhome' },
                        { value: 'condo', label: 'Condo' },
                        { value: 'any', label: 'Open to all' },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => updateAnswer('property_type', opt.value)}
                          className={`option-card p-3.5 rounded-xl text-center text-sm font-medium transition-all ${
                            answers.property_type === opt.value ? 'border-slate-900 bg-slate-50 shadow-sm' : ''
                          }`}
                          data-selected={answers.property_type === opt.value}
                          data-testid={`property-${opt.value}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4 - Priorities */}
            {step === 4 && (
              <div data-testid="step-4">
                <p className="font-mono text-xs text-slate-400 mb-2">04</p>
                <h2 className="text-3xl md:text-4xl tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  What matters <span className="italic">most?</span>
                </h2>
                <p className="text-sm text-slate-500 mb-8">Select all that apply. This drives matching.</p>
                <div className="grid grid-cols-1 gap-2">
                  {PRIORITY_OPTIONS.map(opt => (
                    <label
                      key={opt.value}
                      className="option-card flex items-center gap-3 p-4 rounded-xl cursor-pointer"
                      data-selected={answers.priorities.includes(opt.value)}
                      data-testid={`priority-${opt.value}`}
                    >
                      <Checkbox
                        checked={answers.priorities.includes(opt.value)}
                        onCheckedChange={() => togglePriority(opt.value)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                        answers.priorities.includes(opt.value) ? 'border-slate-900 bg-slate-900' : 'border-slate-300'
                      }`}>
                        {answers.priorities.includes(opt.value) && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Step 5 - Family */}
            {step === 5 && (
              <div data-testid="step-5">
                <p className="font-mono text-xs text-slate-400 mb-2">05</p>
                <h2 className="text-3xl md:text-4xl tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  Family <span className="italic">details</span>
                </h2>
                <p className="text-sm text-slate-500 mb-8">Optional — refines school and space recommendations.</p>
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500 font-medium mb-2 block">Bedrooms</Label>
                      <Select value={String(answers.beds_min)} onValueChange={(v) => updateAnswer('beds_min', Number(v))}>
                        <SelectTrigger className="bg-white rounded-xl h-11 text-sm" data-testid="beds-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n}+ beds</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 font-medium mb-2 block">Bathrooms</Label>
                      <Select value={String(answers.baths_min)} onValueChange={(v) => updateAnswer('baths_min', Number(v))}>
                        <SelectTrigger className="bg-white rounded-xl h-11 text-sm" data-testid="baths-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4].map(n => <SelectItem key={n} value={String(n)}>{n}+ baths</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 font-medium mb-2 block">School preference</Label>
                    <Select value={answers.school_preference} onValueChange={(v) => updateAnswer('school_preference', v)}>
                      <SelectTrigger className="bg-white rounded-xl h-11 text-sm" data-testid="school-pref-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No preference</SelectItem>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="catholic">Catholic</SelectItem>
                        <SelectItem value="french">French Immersion</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 font-medium mb-2 block">Kids ages (optional)</Label>
                    <Input
                      placeholder="e.g. 4, 7, 11"
                      className="bg-white rounded-xl h-11 text-sm"
                      value={answers.kids_ages?.join(', ') || ''}
                      onChange={(e) => {
                        const ages = e.target.value.split(',').map(a => parseInt(a.trim())).filter(a => !isNaN(a));
                        updateAnswer('kids_ages', ages);
                      }}
                      data-testid="kids-ages-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 6 - Readiness */}
            {step === 6 && (
              <div data-testid="step-6">
                <p className="font-mono text-xs text-slate-400 mb-2">06</p>
                <h2 className="text-3xl md:text-4xl tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  Purchase <span className="italic">readiness</span>
                </h2>
                <p className="text-sm text-slate-500 mb-8">Helps us tailor your results.</p>
                <div className="space-y-5">
                  <div>
                    <Label className="text-xs text-slate-500 font-medium mb-3 block">Pre-approval status</Label>
                    <RadioGroup value={answers.preapproval_status} onValueChange={(v) => updateAnswer('preapproval_status', v)} className="space-y-2">
                      {[
                        { value: 'yes', label: 'Pre-approved', sub: 'Ready to offer' },
                        { value: 'in_progress', label: 'In progress', sub: 'Application started' },
                        { value: 'no', label: 'Not yet', sub: 'Haven\'t started' },
                      ].map(opt => (
                        <label key={opt.value} className="option-card flex items-center gap-3 p-4 rounded-xl cursor-pointer" data-selected={answers.preapproval_status === opt.value} data-testid={`preapproval-${opt.value}`}>
                          <RadioGroupItem value={opt.value} className="sr-only" />
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                            answers.preapproval_status === opt.value ? 'border-slate-900 bg-slate-900' : 'border-slate-300'
                          }`}>
                            {answers.preapproval_status === opt.value && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-700">{opt.label}</p>
                            <p className="text-[11px] text-slate-400">{opt.sub}</p>
                          </div>
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                  <label className="option-card flex items-center gap-3 p-4 rounded-xl cursor-pointer" data-selected={answers.working_with_realtor} data-testid="working-with-realtor">
                    <Checkbox checked={answers.working_with_realtor} onCheckedChange={(v) => updateAnswer('working_with_realtor', v)} className="sr-only" />
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                      answers.working_with_realtor ? 'border-slate-900 bg-slate-900' : 'border-slate-300'
                    }`}>
                      {answers.working_with_realtor && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="text-sm font-medium text-slate-700">I'm already working with a realtor</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom nav */}
        <div className="border-t border-slate-100 px-6 py-4 bg-white">
          <div className="max-w-md mx-auto flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={step === 1}
              className="text-slate-400 hover:text-slate-900 rounded-full h-10"
              data-testid="btn-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={!canProceed() || saving || completing}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-7 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all disabled:opacity-40"
              data-testid="btn-next"
            >
              {completing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
              ) : step === TOTAL_STEPS ? (
                <>Get Results <ArrowRight className="ml-2 w-4 h-4" /></>
              ) : (
                <>Continue <ArrowRight className="ml-2 w-4 h-4" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
